import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import PublicIcon from '@mui/icons-material/Public';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

const TabPanel = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ p: 4 }}>{children}</Box>}
    </div>
  );
};

export const HolidaysSection = ({ selectedHolidayTab, setSelectedHolidayTab }) => {
  const handleHolidayTabChange = (event, newValue) => {
    setSelectedHolidayTab(newValue);
  };

  const holidaysByCountry = {
    india: [
      { name: "Republic Day", date: "Jan 26, 2026", type: "National" },
      { name: "Holi", date: "Mar 14, 2026", type: "Festival" },
      { name: "Independence Day", date: "Aug 15, 2026", type: "National" },
      { name: "Diwali", date: "Oct 20, 2026", type: "Festival" },
      { name: "Christmas", date: "Dec 25, 2026", type: "Festival" },
    ],
    singapore: [
      { name: "New Year's Day", date: "Jan 1, 2026", type: "Public" },
      { name: "Chinese New Year", date: "Jan 29-30, 2026", type: "Festival" },
      { name: "Good Friday", date: "Apr 3, 2026", type: "Public" },
      { name: "Labour Day", date: "May 1, 2026", type: "Public" },
      { name: "National Day", date: "Aug 9, 2026", type: "National" },
      { name: "Deepavali", date: "Oct 20, 2026", type: "Festival" },
      { name: "Christmas", date: "Dec 25, 2026", type: "Public" },
    ],
    uae: [
      { name: "New Year's Day", date: "Jan 1, 2026", type: "Public" },
      { name: "Eid al-Fitr", date: "Apr 10-12, 2026", type: "Islamic" },
      { name: "Eid al-Adha", date: "Jun 16-18, 2026", type: "Islamic" },
      { name: "Islamic New Year", date: "Jul 7, 2026", type: "Islamic" },
      { name: "National Day", date: "Dec 2-3, 2026", type: "National" },
    ],
    australia: [
      { name: "New Year's Day", date: "Jan 1, 2026", type: "Public" },
      { name: "Australia Day", date: "Jan 26, 2026", type: "National" },
      { name: "Good Friday", date: "Apr 3, 2026", type: "Public" },
      { name: "Easter Monday", date: "Apr 6, 2026", type: "Public" },
      { name: "Anzac Day", date: "Apr 25, 2026", type: "National" },
      { name: "Queen's Birthday", date: "Jun 8, 2026", type: "Public" },
      { name: "Christmas Day", date: "Dec 25, 2026", type: "Public" },
      { name: "Boxing Day", date: "Dec 26, 2026", type: "Public" },
    ],
  };

  const countryData = [
    { key: 'india', name: 'India', flag: '🇮🇳', color: '#ff9933' },
    { key: 'singapore', name: 'Singapore', flag: '🇸🇬', color: '#ed2939' },
    { key: 'uae', name: 'UAE', flag: '🇦🇪', color: '#00732f' },
    { key: 'australia', name: 'Australia', flag: '🇦🇺', color: '#012169' },
  ];

  const getTypeColor = (type) => {
    switch (type) {
      case 'National':
        return '#ef4444';
      case 'Festival':
        return '#f59e0b';
      case 'Public':
        return '#10b981';
      case 'Islamic':
        return '#8b5cf6';
      default:
        return '#6b7280';
    }
  };

  return (
    <Box
      sx={{
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
        py: 10,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: 400,
          height: 400,
          background: 'radial-gradient(circle, rgba(102,126,234,0.15) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(80px)',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <CalendarMonthIcon sx={{ fontSize: 60, color: '#fbbf24', mb: 2 }} />
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              color: '#fff',
              mb: 2,
              fontSize: { xs: '2rem', md: '3rem' },
            }}
          >
            Global Holiday Calendar 2026
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255,255,255,0.7)',
              maxWidth: 600,
              mx: 'auto',
            }}
          >
            Plan ahead with holidays across our global offices
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: 'divider', background: '#f8fafc' }}>
            <Tabs
              value={selectedHolidayTab}
              onChange={handleHolidayTabChange}
              centered
              variant="fullWidth"
              sx={{
                '& .MuiTab-root': {
                  fontWeight: 700,
                  fontSize: '1rem',
                  textTransform: 'none',
                  py: 2.5,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'rgba(102,126,234,0.05)',
                  },
                },
                '& .Mui-selected': {
                  color: '#667eea !important',
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                  background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                },
              }}
            >
              {countryData.map((country) => (
                <Tab
                  key={country.key}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography sx={{ fontSize: '1.5rem' }}>{country.flag}</Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>
                        {country.name}
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Box>

          {countryData.map((country, index) => (
            <TabPanel key={country.key} value={selectedHolidayTab} index={index}>
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <PublicIcon sx={{ color: country.color, fontSize: 32 }} />
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2937' }}>
                    {country.name} Public Holidays
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    {holidaysByCountry[country.key].length} holidays in 2026
                  </Typography>
                </Box>
              </Box>

              <List sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {holidaysByCountry[country.key].map((holiday, idx) => (
                  <ListItem
                    key={idx}
                    sx={{
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                      border: '2px solid #e5e7eb',
                      padding: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'pointer',
                      '&:hover': {
                        background: `linear-gradient(135deg, ${getTypeColor(holiday.type)}08 0%, #ffffff 100%)`,
                        border: `2px solid ${getTypeColor(holiday.type)}40`,
                        transform: 'translateX(12px)',
                        boxShadow: `0 8px 24px ${getTypeColor(holiday.type)}20`,
                      },
                    }}
                  >
                    <ListItemIcon>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: 3,
                          background: `linear-gradient(135deg, ${getTypeColor(holiday.type)}20 0%, ${getTypeColor(holiday.type)}10 100%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <EventIcon sx={{ color: getTypeColor(holiday.type), fontSize: 28 }} />
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      sx={{ ml: 2 }}
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: '#1f2937',
                              fontSize: '1.1rem',
                            }}
                          >
                            {holiday.name}
                          </Typography>
                          <Chip
                            label={holiday.type}
                            size="small"
                            sx={{
                              background: `linear-gradient(135deg, ${getTypeColor(holiday.type)} 0%, ${getTypeColor(holiday.type)}dd 100%)`,
                              color: '#fff',
                              fontWeight: 700,
                              fontSize: '0.75rem',
                              height: 24,
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <CalendarMonthIcon sx={{ fontSize: 16, color: '#6b7280' }} />
                          <Typography
                            variant="body1"
                            sx={{
                              color: '#6b7280',
                              fontWeight: 600,
                              fontSize: '0.95rem',
                            }}
                          >
                            {holiday.date}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </TabPanel>
          ))}
        </Paper>

        {/* Summary Stats */}
        <Box
          sx={{
            mt: 6,
            display: 'flex',
            justifyContent: 'center',
            gap: 4,
            flexWrap: 'wrap',
          }}
        >
          {countryData.map((country) => (
            <Paper
              key={country.key}
              elevation={0}
              sx={{
                px: 4,
                py: 2,
                borderRadius: 3,
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
                textAlign: 'center',
              }}
            >
              <Typography sx={{ fontSize: '1.5rem', mb: 0.5 }}>{country.flag}</Typography>
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.5rem' }}>
                {holidaysByCountry[country.key].length}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>
                Holidays
              </Typography>
            </Paper>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default HolidaysSection;